import { useEffect, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import type { DatosAvisoPrivacidad } from '@shared/privacidad';

/**
 * Sprint 167 — El aviso de privacidad que lee el cliente del enlace.
 *
 * Lo escribe el sistema con los datos que el dueño puso en Configuración → Privacidad de clientes, y
 * dice lo que el sistema HACE (qué guarda, para qué, cuánto tiempo, a quién se le pide un borrado).
 * Si el texto y el sistema se separan, el aviso miente: por eso el plazo sale de la misma constante
 * que usa el borrado automático.
 *
 * Lenguaje de mostrador a propósito: quien lo lee está pidiendo pollo desde su celular.
 */

/** Fecha de esta versión del texto. Si el texto cambia, se cambia esta fecha. */
export const AVISO_ACTUALIZADO = '28 de octubre de 2026';

export function useAvisoPrivacidad(activo = true): { aviso: DatosAvisoPrivacidad | null; error: boolean } {
  const [aviso, setAviso] = useState<DatosAvisoPrivacidad | null>(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!activo || aviso) return;
    let vivo = true;
    void (async () => {
      try {
        const r = await fetch('/api/publico/privacidad');
        const j = (await r.json()) as { ok: boolean; aviso?: DatosAvisoPrivacidad };
        if (vivo) {
          if (j.ok && j.aviso) setAviso(j.aviso);
          else setError(true);
        }
      } catch {
        if (vivo) setError(true);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [activo, aviso]);
  return { aviso, error };
}

function Apartado({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <section className="mt-5">
      <h2 className="text-base font-bold text-slate-900">{titulo}</h2>
      <div className="mt-1 space-y-2 text-sm leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

export function AvisoPrivacidadTexto({ aviso }: { aviso: DatosAvisoPrivacidad }) {
  const meses = aviso.mesesConservar;
  return (
    <article>
      <h1 className="text-xl font-extrabold text-slate-900">Aviso de privacidad</h1>
      <p className="text-sm text-slate-700">{aviso.nombreComercial}</p>

      <Apartado titulo="Quién cuida tus datos">
        <p>
          <b>{aviso.responsable || aviso.nombreComercial}</b>
          {aviso.domicilio ? <>, con domicilio en {aviso.domicilio},</> : null} es responsable de los datos
          personales que nos das al hacer un pedido.
        </p>
      </Apartado>

      <Apartado titulo="Qué datos te pedimos">
        <p>
          Tu nombre, tu WhatsApp, tu dirección y las referencias para llegar. Tu ubicación, sólo si tocas
          “Compartir mi ubicación”. No te pedimos ningún dato sensible.
        </p>
      </Apartado>

      <Apartado titulo="Para qué los usamos">
        <p>
          Para preparar tu pedido, confirmarlo y cobrarlo, llevártelo y avisarte cómo va. Y para reconocerte la
          próxima vez que pidas, sin volver a preguntarte tu dirección. Sin estos datos no podemos atender un
          pedido a domicilio.
        </p>
        <p>
          <b>Promociones:</b> sólo si tú nos dices que sí, te mandamos promociones por WhatsApp. Puedes pedir que
          dejemos de hacerlo cuando quieras, y eso no cambia nada en tus pedidos.
        </p>
      </Apartado>

      <Apartado titulo="Con quién se comparten">
        <p>
          No vendemos tus datos ni se los damos a otras empresas para su propio uso. La persona que te lleva el
          pedido recibe tu nombre, tu dirección y tu teléfono, sólo para entregártelo.
        </p>
        <p>
          Los guardan, por encargo nuestro y con obligación de confidencialidad, los proveedores que operan este
          sistema de pedidos (el servidor donde se guarda y su mantenimiento).
        </p>
      </Apartado>

      <Apartado titulo="Cuánto tiempo los guardamos">
        <p>
          Mientras sigas pidiendo. Si pasan {meses} meses sin que hagas un pedido, tus datos se borran solos.
          El registro de la venta (qué se vendió y por cuánto) se conserva sin tus datos, porque es parte de
          nuestra contabilidad.
        </p>
        <p>
          Esta página también guarda tu nombre y dirección <b>en tu propio teléfono</b>, para no pedírtelos cada
          vez. Los puedes borrar desde el formulario del pedido.
        </p>
      </Apartado>

      <Apartado titulo="Tus derechos">
        <p>
          Puedes pedirnos ver tus datos, corregirlos, borrarlos u oponerte a que los usemos, y retirar tu
          permiso. Escríbenos
          {aviso.contacto ? (
            <>
              {' '}a <b>{aviso.contacto}</b>
            </>
          ) : (
            ' por el mismo WhatsApp donde haces tus pedidos'
          )}{' '}
          con tu nombre y el teléfono con el que pediste. Te respondemos en un máximo de 20 días hábiles.
        </p>
        <p>
          Si no quedas conforme con la respuesta, puedes acudir a la Secretaría Anticorrupción y Buen Gobierno,
          la autoridad de protección de datos personales.
        </p>
      </Apartado>

      <Apartado titulo="Cambios a este aviso">
        <p>Si este aviso cambia, lo verás en esta misma página. Última actualización: {AVISO_ACTUALIZADO}.</p>
      </Apartado>
    </article>
  );
}

/** El aviso completo encima del menú, sin salir de él (salir perdería la canasta). */
export function AvisoPrivacidadHoja({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const { aviso, error } = useAvisoPrivacidad(abierto);
  if (!abierto) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" role="dialog" aria-modal="true" aria-label="Aviso de privacidad">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-2xl bg-white p-5 sm:rounded-2xl">
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onCerrar}
            className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
            aria-label="Cerrar el aviso"
          >
            <X className="h-6 w-6" />
          </button>
        </div>
        {aviso ? (
          <AvisoPrivacidadTexto aviso={aviso} />
        ) : error ? (
          <p className="text-sm text-slate-700">No pudimos cargar el aviso. Inténtalo de nuevo en un momento.</p>
        ) : (
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-500" />
        )}
        <button
          type="button"
          onClick={onCerrar}
          className="mt-6 min-h-[52px] w-full rounded-xl bg-primary text-base font-bold text-white"
        >
          Entendido, seguir con mi pedido
        </button>
      </div>
    </div>
  );
}
